import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { 
  Search, 
  Filter,
  ExternalLink,
  Clock,
  DollarSign,
  TrendingUp,
  CheckCircle,
  Brain,
  Lightbulb,
  FileText,
  Workflow,
  Calendar,
  Users,
  Mail,
  Building,
  BarChart3,
  Code,
  Home,
  ArrowRight,
  Zap,
  Target
} from 'lucide-react'
import { aiTools, toolCategories, subcategories, AITool } from '@/data/aiToolsData'

const iconMap = {
  Brain,
  Lightbulb,
  FileText,
  Workflow,
  Calendar,
  Users,
  Mail,
  Building,
  BarChart3,
  Code
}

export function AIToolsReference() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'crawl' | 'walk' | 'run'>('all')
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('all')
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all')

  const filteredTools = useMemo(() => {
    return aiTools.filter(tool => {
      const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           tool.bestFor.some(use => use.toLowerCase().includes(searchTerm.toLowerCase()))
      
      const matchesCategory = selectedCategory === 'all' || tool.category === selectedCategory
      const matchesSubcategory = selectedSubcategory === 'all' || tool.subcategory === selectedSubcategory
      const matchesDifficulty = selectedDifficulty === 'all' || tool.difficulty === selectedDifficulty

      return matchesSearch && matchesCategory && matchesSubcategory && matchesDifficulty
    })
  }, [searchTerm, selectedCategory, selectedSubcategory, selectedDifficulty])

  const uniqueSubcategories = Array.from(new Set(aiTools.map(tool => tool.subcategory)))

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 bg-green-100 dark:bg-green-900/20'
      case 'intermediate': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20'
      case 'advanced': return 'text-red-600 bg-red-100 dark:bg-red-900/20'
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20'
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'crawl': return 'text-green-600 bg-green-100 dark:bg-green-900/20 border-green-200 dark:border-green-800'
      case 'walk': return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
      case 'run': return 'text-purple-600 bg-purple-100 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800'
    }
  }

  const getROIIcon = (roi: string) => {
    switch (roi) {
      case 'very high': return <TrendingUp className="w-4 h-4 text-green-600" />
      case 'high': return <TrendingUp className="w-4 h-4 text-blue-600" />
      case 'medium': return <TrendingUp className="w-4 h-4 text-yellow-600" />
      default: return <TrendingUp className="w-4 h-4 text-gray-600" />
    }
  }

  const renderToolCard = (tool: AITool) => {
    const subcategoryInfo = subcategories[tool.subcategory as keyof typeof subcategories]
    const IconComponent = iconMap[subcategoryInfo?.icon as keyof typeof iconMap] || Brain

    return (
      <Card key={tool.id} className="h-full hover:shadow-lg transition-shadow">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <IconComponent className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">{tool.name}</CardTitle>
                <CardDescription className="text-sm">{tool.subcategory}</CardDescription>
              </div>
            </div>
            <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getCategoryColor(tool.category)}`}>
              {tool.category.toUpperCase()}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {tool.description}
          </p>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span>{tool.timeToImplement}</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-muted-foreground" />
                <span>{tool.monthlyInvestment}</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {getROIIcon(tool.roi)}
                <span className="capitalize">{tool.roi} ROI</span>
              </div>
              <div className={`px-2 py-1 rounded text-xs ${getDifficultyColor(tool.difficulty)}`}>
                {tool.difficulty}
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium mb-2">Best For:</h4>
            <div className="flex flex-wrap gap-1">
              {tool.bestFor.slice(0, 3).map((use, index) => (
                <span key={index} className="px-2 py-1 bg-muted rounded text-xs">
                  {use}
                </span>
              ))}
              {tool.bestFor.length > 3 && (
                <span className="px-2 py-1 bg-muted rounded text-xs">
                  +{tool.bestFor.length - 3} more
                </span>
              )}
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium mb-2">Getting Started:</h4>
            <p className="text-xs text-muted-foreground">
              {tool.gettingStarted}
            </p>
          </div>
          
          <div className="flex gap-2 pt-2">
            {tool.website && (
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1 gap-2" 
                onClick={() => window.open(tool.website, '_blank')}
              >
                <ExternalLink className="w-3 h-3" />
                Visit Site
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-md dark:bg-gray-900/80 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Zap className="w-6 h-6 text-primary" />
                <span className="font-semibold">AI Tools Reference</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="gap-2"
              >
                <Home className="w-4 h-4" />
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="text-center space-y-6 mb-12">
          <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
            <Target className="w-10 h-10 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl font-bold mb-2">AI Tools Reference Guide</h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive directory of AI tools and platforms organized by implementation complexity. 
              Find the right tool for your business needs and current skill level.
            </p>
          </div>
        </div>

        {/* Framework Overview */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {Object.entries(toolCategories).map(([key, category]) => (
            <Card 
              key={key} 
              className={`cursor-pointer transition-all hover:scale-105 ${
                selectedCategory === key ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => setSelectedCategory(selectedCategory === key ? 'all' : key as any)}
            >
              <CardHeader className="text-center">
                <CardTitle className={`text-xl ${getCategoryColor(key).split(' ')[0]}`}>
                  {category.title}
                </CardTitle>
                <CardDescription>{category.subtitle}</CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-3">
                <p className="text-sm text-muted-foreground">{category.description}</p>
                <div className="grid grid-cols-1 gap-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Timeline:</span>
                    <span className="font-medium">{category.timeframe}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Investment:</span>
                    <span className="font-medium">{category.investment}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Difficulty:</span>
                    <span className="font-medium">{category.difficulty}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filter Tools
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search tools..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value as any)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Categories</option>
                  <option value="crawl">Crawl Phase</option>
                  <option value="walk">Walk Phase</option>
                  <option value="run">Run Phase</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Subcategory</label>
                <select
                  value={selectedSubcategory}
                  onChange={(e) => setSelectedSubcategory(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Types</option>
                  {uniqueSubcategories.map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Difficulty</label>
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Levels</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Summary */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-sm text-muted-foreground">
            Showing {filteredTools.length} of {aiTools.length} tools
          </div>
          {(searchTerm || selectedCategory !== 'all' || selectedSubcategory !== 'all' || selectedDifficulty !== 'all') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearchTerm('')
                setSelectedCategory('all')
                setSelectedSubcategory('all')
                setSelectedDifficulty('all')
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Tools Grid */}
        {filteredTools.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTools.map(renderToolCard)}
          </div>
        ) : (
          <Card className="text-center py-12">
            <CardContent>
              <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No tools found</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your search criteria or clearing the filters.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('')
                  setSelectedCategory('all')
                  setSelectedSubcategory('all')
                  setSelectedDifficulty('all')
                }}
              >
                Clear All Filters
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Call to Action */}
        <Card className="mt-12">
          <CardContent className="text-center py-8">
            <h3 className="text-xl font-semibold mb-4">Ready to Start Your AI Journey?</h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              These tools are organized by the Crawl-Walk-Run framework. Start with Crawl phase tools 
              for immediate wins, then progress to more advanced solutions as you build confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate('/onboarding')} className="gap-2">
                <ArrowRight className="w-4 h-4" />
                Start AI Journey
              </Button>
              <Button variant="outline" onClick={() => navigate('/overview')} className="gap-2">
                <CheckCircle className="w-4 h-4" />
                View AI Overview
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}